// Chart Tool Registry — every drawing object type of the Felacoo BOS chart engine.
export type DrawingType =
  // lines
  | "trend" | "ray" | "extline" | "infoline" | "trendangle" | "hline" | "hray" | "vline" | "crossline"
  // channels
  | "channel" | "flatchannel" | "disjoint" | "regression"
  // pitchforks
  | "pitchfork" | "schiff" | "mschiff" | "insidefork" | "pitchfan"
  // shapes & brushes
  | "rect" | "rotrect" | "circle" | "triangle" | "arc" | "curve" | "dcurve" | "polyline" | "path" | "brush" | "highlighter"
  | "arrow" | "arrowup" | "arrowdown" | "arrowmarkl" | "arrowmarkr"
  // text & annotations
  | "text" | "anchoredtext" | "note" | "pricelabel" | "pricenote" | "signpost" | "flag" | "callout" | "emoji"
  // forecasting & measuring
  | "forecast" | "projection" | "daterange" | "pricerange" | "measure" | "rr" | "longpos" | "shortpos"
  // volume
  | "avwap" | "fixedvp" | "anchoredvp"
  // fibonacci
  | "fib" | "fibext" | "fibchannel" | "fibtimezone" | "fibtimeext" | "fibspeedfan" | "fibcircles" | "fibarcs" | "fibspiral" | "fibwedge"
  // gann
  | "gannbox" | "gannsquare" | "gannfan" | "gannfixed"
  // patterns, waves, cycles
  | "xabcd" | "cypher" | "abcd" | "tripattern" | "threedrives" | "headshoulders"
  | "elliottimpulse" | "elliottcorrection" | "elliotttriangle" | "elliottdouble" | "elliotttriple"
  | "cyclelines" | "timecycles" | "sineline"
  // Felacoo BOS
  | "fvgzone" | "bosmark" | "chochmark" | "liqline" | "sweepmark" | "obzone" | "eqhl" | "swingmark" | "tickrange" | "confluencestamp"
  // Deriv
  | "hlbarrier" | "touchbarrier" | "tickcontract" | "expirymark";

export type CursorTool = "select" | "cross" | "dot" | "eraser";
export type ToolId = CursorTool | DrawingType;

// Time-anchored point: survives new candles, timeframe switches and reloads.
export interface DPoint { time: number; price: number }

export interface DrawingStyle {
  color: string;
  width: number;
  dash: number[];
  fill: number; // 0..1 alpha for area fills
  fontSize: number;
}

export interface RRMeta { balance: number; riskPct: number }

export interface DrawingObject {
  id: string;
  type: DrawingType;
  symbol: string;
  timeframe: string | null; // null = shown on every timeframe of the symbol
  points: DPoint[];
  style: DrawingStyle;
  visible: boolean;
  locked: boolean;
  text?: string;
  meta?: Partial<RRMeta> & Record<string, unknown>;
}

export interface ToolSpec {
  label: string;
  points: number;          // clicks needed (variable/freehand: minimum)
  variable?: boolean;      // click to add points, double-click / Enter to finish
  freehand?: boolean;      // press-drag-release
  text?: string;           // default text
  meta?: Record<string, unknown>;
  color?: string;
}

const T = (label: string, points: number, extra: Partial<ToolSpec> = {}): ToolSpec => ({ label, points, ...extra });

export const TOOLS: Record<DrawingType, ToolSpec> = {
  trend: T("Trend line", 2), ray: T("Ray", 2), extline: T("Extended line", 2), infoline: T("Info line", 2), trendangle: T("Trend angle", 2),
  hline: T("Horizontal line", 1), hray: T("Horizontal ray", 1), vline: T("Vertical line", 1), crossline: T("Cross line", 1),
  channel: T("Parallel channel", 3), flatchannel: T("Flat top / bottom", 3), disjoint: T("Disjoint channel", 4), regression: T("Regression trend", 2),
  pitchfork: T("Pitchfork", 3), schiff: T("Schiff pitchfork", 3), mschiff: T("Modified Schiff pitchfork", 3), insidefork: T("Inside pitchfork", 3), pitchfan: T("Pitchfan", 3),
  rect: T("Rectangle", 2), rotrect: T("Rotated rectangle", 3), circle: T("Ellipse", 2), triangle: T("Triangle", 3), arc: T("Arc", 3), curve: T("Curve", 3), dcurve: T("Double curve", 4),
  polyline: T("Polyline", 2, { variable: true }), path: T("Path", 2, { variable: true }), brush: T("Brush", 2, { freehand: true }), highlighter: T("Highlighter", 2, { freehand: true, color: "#F5A623" }),
  arrow: T("Arrow", 2), arrowup: T("Arrow marker up", 1, { color: "#00C076" }), arrowdown: T("Arrow marker down", 1, { color: "#FF3B30" }), arrowmarkl: T("Arrow mark left", 1), arrowmarkr: T("Arrow mark right", 1),
  text: T("Text", 1, { text: "Text" }), anchoredtext: T("Anchored text", 1, { text: "Anchored text" }), note: T("Note", 1, { text: "Note" }), pricelabel: T("Price label", 1),
  pricenote: T("Price note", 2, { text: "" }), signpost: T("Signpost", 1, { text: "Signpost" }), flag: T("Flag mark", 1), callout: T("Callout", 2, { text: "Note" }), emoji: T("Emoji", 1, { meta: { emoji: "🔥" } }),
  forecast: T("Forecast", 2), projection: T("Projection", 3), daterange: T("Date range", 2), pricerange: T("Price range", 2), measure: T("Date & price range", 2),
  rr: T("Risk / Reward", 2, { meta: { balance: 10000, riskPct: 1 } }), longpos: T("Long position", 2, { meta: { balance: 10000, riskPct: 1 } }), shortpos: T("Short position", 2, { meta: { balance: 10000, riskPct: 1 } }),
  avwap: T("Anchored VWAP", 1), fixedvp: T("Fixed range volume profile", 2), anchoredvp: T("Anchored volume profile", 1),
  fib: T("Fib retracement", 2), fibext: T("Trend-based fib extension", 3), fibchannel: T("Fib channel", 3), fibtimezone: T("Fib time zone", 2), fibtimeext: T("Trend-based fib time", 3),
  fibspeedfan: T("Fib speed resistance fan", 2), fibcircles: T("Fib circles", 2), fibarcs: T("Fib speed resistance arcs", 2), fibspiral: T("Fib spiral", 2), fibwedge: T("Fib wedge", 3),
  gannbox: T("Gann box", 2), gannsquare: T("Gann square", 2), gannfan: T("Gann fan", 2), gannfixed: T("Gann square fixed", 2),
  xabcd: T("XABCD pattern", 5), cypher: T("Cypher pattern", 5), abcd: T("ABCD pattern", 4), tripattern: T("Triangle pattern", 4), threedrives: T("Three drives pattern", 7), headshoulders: T("Head and shoulders", 7),
  elliottimpulse: T("Elliott impulse wave (12345)", 6), elliottcorrection: T("Elliott correction wave (ABC)", 4), elliotttriangle: T("Elliott triangle wave (ABCDE)", 6),
  elliottdouble: T("Elliott double combo (WXY)", 4), elliotttriple: T("Elliott triple combo (WXYXZ)", 6),
  cyclelines: T("Cyclic lines", 2), timecycles: T("Time cycles", 2), sineline: T("Sine line", 2),
  fvgzone: T("FVG zone", 2, { color: "#A855F7" }), bosmark: T("BOS marker", 2, { color: "#00C076" }), chochmark: T("CHoCH marker", 2, { color: "#FF3B30" }),
  liqline: T("Liquidity line", 1, { color: "#3B82F6" }), sweepmark: T("Liquidity sweep", 1, { color: "#3B82F6" }), obzone: T("Order block", 2, { color: "#EC4899" }),
  eqhl: T("Equal highs / lows", 2, { color: "#3B82F6" }), swingmark: T("Swing high / low", 1), tickrange: T("Tick range (speed · dominance · imbalance)", 2),
  confluencestamp: T("Confluence stamp", 1),
  hlbarrier: T("Higher / Lower barrier", 1, { color: "#3B82F6" }), touchbarrier: T("Touch / No Touch barrier", 1, { color: "#A855F7", meta: { kind: "touch" } }),
  tickcontract: T("Tick contract (Rise / Fall)", 1, { meta: { ticks: 5, direction: "rise" } }), expirymark: T("Expiry marker", 1, { color: "#FF3B30" }),
};

export const POINTS_NEEDED: Record<DrawingType, number> = Object.fromEntries(Object.entries(TOOLS).map(([k, v]) => [k, v.points])) as Record<DrawingType, number>;
export const TOOL_LABELS: Record<DrawingType, string> = Object.fromEntries(Object.entries(TOOLS).map(([k, v]) => [k, v.label])) as Record<DrawingType, string>;

export const DEFAULT_STYLE: DrawingStyle = { color: "#F5A623", width: 1.5, dash: [], fill: 0.12, fontSize: 12 };

export const DRAWING_COLORS = ["#F5A623", "#00C076", "#FF3B30", "#3B82F6", "#A855F7", "#EC4899", "#E5E7EB", "#7C879E"];

let seq = 0;
export const newDrawingId = () => `d-${Date.now().toString(36)}-${(seq++).toString(36)}`;

export function createDrawing(type: DrawingType, symbol: string, points: DPoint[], patch: Partial<DrawingObject> = {}): DrawingObject {
  const spec = TOOLS[type];
  const base: DrawingObject = {
    id: newDrawingId(), type, symbol, timeframe: null, points, style: { ...DEFAULT_STYLE, color: spec.color ?? DEFAULT_STYLE.color }, visible: true, locked: false,
  };
  if (type === "highlighter") base.style.width = 14;
  if (type === "brush") base.style.width = 2;
  if (spec.text != null) base.text = spec.text;
  if (spec.meta) base.meta = { ...spec.meta };
  if ((type === "rr" || type === "longpos" || type === "shortpos") && points.length === 2) base.points = rrPoints(points[0], points[1], type);
  return { ...base, ...patch, meta: patch.meta ? { ...base.meta, ...patch.meta } : base.meta };
}

// RR / positions: [entry, target(+right edge time), stop]. Default stop = 1R for a 2R target when created from 2 clicks.
export function rrPoints(entry: DPoint, target: DPoint, type: DrawingType = "rr"): DPoint[] {
  let t = target;
  if (type === "longpos" && t.price <= entry.price) t = { ...t, price: entry.price + Math.abs(entry.price - t.price) };
  if (type === "shortpos" && t.price >= entry.price) t = { ...t, price: entry.price - Math.abs(entry.price - t.price) };
  const reward = t.price - entry.price;
  return [entry, t, { time: t.time, price: entry.price - reward / 2 }];
}

export const isPositionTool = (t: DrawingType) => t === "rr" || t === "longpos" || t === "shortpos";

export interface RRInfo {
  side: "long" | "short"; entry: number; stop: number; target: number;
  risk: number; reward: number; ratio: number;
  lot: number | null; riskAmt: number | null; rewardAmt: number | null;
}

export function rrInfo(d: DrawingObject): RRInfo | null {
  if (!isPositionTool(d.type) || d.points.length < 3) return null;
  const [e, t, s] = d.points;
  const side = t.price >= e.price ? "long" : "short";
  const risk = Math.abs(e.price - s.price), reward = Math.abs(t.price - e.price);
  const ratio = risk > 0 ? reward / risk : 0;
  const bal = Number(d.meta?.balance ?? 0), pct = Number(d.meta?.riskPct ?? 0);
  const riskAmt = bal > 0 && pct > 0 ? (bal * pct) / 100 : null;
  const lot = riskAmt != null && risk > 0 ? riskAmt / (risk * 100) : null; // pnl = points × lot × 100
  return { side, entry: e.price, stop: s.price, target: t.price, risk, reward, ratio, lot, riskAmt, rewardAmt: riskAmt != null ? riskAmt * ratio : null };
}
